import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi, agentPromptsApi, agentMcpConnectionsApi, agentJournalsApi } from '@/services/api';
import type { AgentJournalFilters, CreateAgentInput, UpdateAgentInput } from '@/types';

export const useAgents = (page = 1, limit = 20, filters?: { isActive?: boolean; search?: string; modelConfigId?: string }) => {
  return useQuery({
    queryKey: ['agents', page, limit, filters],
    queryFn: () => agentsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useAgent = (id: string | undefined) => {
  return useQuery({
    queryKey: ['agent', id],
    queryFn: () => agentsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentInput) =>
      agentsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentInput }) =>
      agentsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent', variables.id] });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

// ============================================================================
// Agent Prompts Hooks
// ============================================================================
export const useAgentPrompts = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ['agent-prompts', agentId],
    queryFn: () => agentPromptsApi.listAgentPrompts(agentId!).then(res => res.data.data),
    enabled: !!agentId,
  });
};

export const useActiveAgentPrompt = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ['active-agent-prompt', agentId],
    queryFn: () => agentPromptsApi.getActivePrompt(agentId!).then(res => res.data.data),
    enabled: !!agentId,
  });
};

export const useAssignPrompt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, promptId }: { agentId: string; promptId: string }) =>
      agentPromptsApi.assignPrompt(agentId, { promptId }).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-prompts', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['active-agent-prompt', variables.agentId] });
    },
  });
};

export const useRemovePrompt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, promptId }: { agentId: string; promptId: string }) =>
      agentPromptsApi.removePrompt(agentId, promptId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-prompts', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['active-agent-prompt', variables.agentId] });
    },
  });
};

// ============================================================================
// Agent MCP Connections Hooks
// ============================================================================
export const useAgentMcpConnections = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ['agent-mcp-connections', agentId],
    queryFn: () => agentMcpConnectionsApi.list(agentId!).then(res => res.data.data),
    enabled: !!agentId,
  });
};

export const useAddAgentMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, mcpConnectionId }: { agentId: string; mcpConnectionId: string }) =>
      agentMcpConnectionsApi.add(agentId, mcpConnectionId).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-mcp-connections', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', variables.agentId] });
    },
  });
};

export const useRemoveAgentMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, mcpConnectionId }: { agentId: string; mcpConnectionId: string }) =>
      agentMcpConnectionsApi.remove(agentId, mcpConnectionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-mcp-connections', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', variables.agentId] });
    },
  });
};

// ============================================================================
// Agent Run History Hooks (task 797)
// ============================================================================

/**
 * One agent's runs, plus a summary of the same window.
 *
 * The window is part of the query key, so switching 24 h → 7 d → 30 d refetches
 * the tiles and the table together rather than leaving a stale aggregate above a
 * fresh table. `staleTime` is short but non-zero: run history changes as runs
 * land, and a service page left open should catch up without hammering the
 * endpoint on every re-render.
 */
export const useAgentJournals = (
  agentId: string | undefined,
  filters?: AgentJournalFilters
) => {
  return useQuery({
    queryKey: ['agent-journals', agentId, filters],
    queryFn: () => agentJournalsApi.list(agentId!, filters).then((res) => res.data.data),
    enabled: !!agentId,
    staleTime: 30_000,
  });
};
